import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';
import { AutomationsApi } from 'app/domains/aya/apis/automations.api';
import { EnumAutomationAction } from 'app/domains/aya/enums/automation-action.enum';
import { EnumAutomationProvisioningStatus } from 'app/domains/aya/enums/automation-provisioning-status.enum';
import { EnumAutomationStatus } from 'app/domains/aya/enums/automation-status.enum';
import { IAutomation } from 'app/domains/aya/interfaces/automation.interface';
import { generateLocalId } from 'app/domains/aya/utils/local-id.util';

const SKELETON_COUNT = 3;
const POLL_INTERVAL_MS = 2000;

const AUTOMATION_ERROR_MESSAGES: { [code: string]: string } = {
  automation_not_found: 'Esta automação não existe mais.',
  automation_version_conflict:
    'Esta automação foi alterada em outro lugar. Recarregue e tente novamente.',
  automation_unavailable:
    'O serviço de automações está indisponível no momento. Tente novamente em instantes.',
};

interface IAutomationRow {
  automation: IAutomation;
  isBusy: boolean;
  active: boolean;
}

@Component({
  selector: 'sgt-automations-section',
  templateUrl: './automations-section.component.html',
  styleUrls: ['./automations-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationsSectionComponent implements OnInit, OnDestroy {
  rows: IAutomationRow[] = [];
  isLoading = false;
  hasError = false;
  isFormOpen = false;
  editingAutomation: IAutomation | null = null;

  readonly skeletonRows = Array.from({ length: SKELETON_COUNT }, (_, index) => index);

  private destroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();
  private isPolling = false;

  constructor(
    private automationsApi: AutomationsApi,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByAutomationId(_: number, row: IAutomationRow): string {
    return row.automation.id;
  }

  needsAttention(automation: IAutomation): boolean {
    return automation.provisioningStatus === EnumAutomationProvisioningStatus.Error;
  }

  isPaused(automation: IAutomation): boolean {
    return automation.status === EnumAutomationStatus.Paused;
  }

  isActive(automation: IAutomation): boolean {
    return automation.status === EnumAutomationStatus.Active;
  }

  isReady(automation: IAutomation): boolean {
    return automation.provisioningStatus === EnumAutomationProvisioningStatus.Ready;
  }

  isSyncing(automation: IAutomation): boolean {
    return (
      automation.provisioningStatus === EnumAutomationProvisioningStatus.Syncing
    );
  }

  canRun(automation: IAutomation): boolean {
    return (
      this.isActive(automation) &&
      this.isReady(automation) &&
      automation.allowedActions.includes(EnumAutomationAction.RunNow)
    );
  }

  runTooltip(automation: IAutomation): string {
    if (!this.isActive(automation)) {
      return 'Ative a automação para executar';
    }
    if (this.isSyncing(automation)) {
      return 'Preparando…';
    }
    return 'Executar agora';
  }

  canDelete(automation: IAutomation): boolean {
    return automation.allowedActions.includes(EnumAutomationAction.Delete);
  }

  canToggle(automation: IAutomation): boolean {
    return (
      automation.allowedActions.includes(EnumAutomationAction.Activate) ||
      automation.allowedActions.includes(EnumAutomationAction.Pause)
    );
  }

  canEdit(automation: IAutomation): boolean {
    return automation.allowedActions.includes(EnumAutomationAction.Edit);
  }

  openCreate(): void {
    this.editingAutomation = null;
    this.isFormOpen = true;
    this.pausePolling();
  }

  openEdit(row: IAutomationRow): void {
    this.editingAutomation = row.automation;
    this.isFormOpen = true;
    this.pausePolling();
  }

  closeForm(): void {
    this.isFormOpen = false;
    this.editingAutomation = null;
    this.syncPolling();
  }

  onSaved(): void {
    this.closeForm();
    this.load();
  }

  toggleActive(row: IAutomationRow, active: boolean): void {
    if (row.isBusy) {
      return;
    }

    const automation = row.automation;
    const previousStatus = automation.status;
    row.isBusy = true;
    row.active = active;
    automation.status = active
      ? EnumAutomationStatus.Active
      : EnumAutomationStatus.Paused;

    const request$ = active
      ? this.automationsApi.activate(automation.id, automation.version)
      : this.automationsApi.pause(automation.id, automation.version);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: updated => {
        row.isBusy = false;
        this.applyAutomation(updated);
        this.cdr.markForCheck();
        this.syncPolling();
      },
      error: error => {
        row.isBusy = false;
        row.active = !active;
        automation.status = previousStatus;
        this.handleMutationError(error, automation.id);
        this.cdr.markForCheck();
      },
    });
  }

  runNow(row: IAutomationRow): void {
    if (row.isBusy) {
      return;
    }

    row.isBusy = true;

    this.automationsApi
      .run(row.automation.id, generateLocalId())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          row.isBusy = false;
          this.toastService.success('Execução iniciada.');
          this.cdr.markForCheck();
        },
        error: error => {
          row.isBusy = false;
          this.handleMutationError(error, row.automation.id);
          this.cdr.markForCheck();
        },
      });
  }

  remove(row: IAutomationRow): void {
    if (row.isBusy) {
      return;
    }

    row.isBusy = true;

    this.automationsApi
      .remove(row.automation.id, row.automation.version)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dropRow(row.automation.id);
          this.toastService.success('Automação excluída.');
          this.cdr.markForCheck();
        },
        error: error => {
          row.isBusy = false;
          this.handleMutationError(error, row.automation.id);
          this.cdr.markForCheck();
        },
      });
  }

  private load(): void {
    this.isLoading = true;
    this.hasError = false;

    this.automationsApi
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: output => {
          this.isLoading = false;
          this.rows = output.items.map(automation => this.buildRow(automation));
          this.cdr.markForCheck();
          this.syncPolling();
        },
        error: () => {
          this.isLoading = false;
          this.hasError = true;
          this.rows = [];
          this.cdr.markForCheck();
        },
      });
  }

  private applyAutomation(automation: IAutomation): void {
    this.rows = this.rows.map(row =>
      row.automation.id === automation.id
        ? { ...row, automation, active: this.isActive(automation) }
        : row,
    );
  }

  private pausePolling(): void {
    this.stopPolling$.next();
    this.isPolling = false;
  }

  private syncPolling(): void {
    const hasSyncing = this.rows.some(row => this.isSyncing(row.automation));

    if (!hasSyncing) {
      this.stopPolling$.next();
      this.isPolling = false;
      return;
    }

    if (this.isPolling) {
      return;
    }

    this.isPolling = true;

    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.automationsApi.list()),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: output => {
          this.reconcile(output.items);
          this.cdr.markForCheck();
          this.stopPollingWhenSettled();
        },
        error: () => {
          this.isPolling = false;
          this.stopPolling$.next();
        },
      });
  }

  private stopPollingWhenSettled(): void {
    const hasSyncing = this.rows.some(row => this.isSyncing(row.automation));
    if (!hasSyncing) {
      this.stopPolling$.next();
      this.isPolling = false;
    }
  }

  private reconcile(automations: IAutomation[]): void {
    this.rows = automations.map(automation => {
      const existing = this.rows.find(row => row.automation.id === automation.id);
      return {
        automation,
        isBusy: existing?.isBusy ?? false,
        active: this.isActive(automation),
      };
    });
  }

  private dropRow(automationId: string): void {
    this.rows = this.rows.filter(row => row.automation.id !== automationId);
  }

  private buildRow(automation: IAutomation): IAutomationRow {
    return {
      automation,
      isBusy: false,
      active: this.isActive(automation),
    };
  }

  private handleMutationError(error: unknown, automationId: string): void {
    if (this.errorCode(error) === 'automation_not_found') {
      this.dropRow(automationId);
    }
    this.toastService.error(this.errorMessage(error));
  }

  private errorCode(error: unknown): string | null {
    const body = (error as { error?: { code?: string; error?: { code?: string } } })?.error;
    return body?.code ?? body?.error?.code ?? null;
  }

  private errorMessage(error: unknown): string {
    const code = this.errorCode(error);
    return (
      (code && AUTOMATION_ERROR_MESSAGES[code]) ||
      this.backendMessage(error) ||
      'Não foi possível concluir a ação.'
    );
  }

  private backendMessage(error: unknown): string | null {
    const body = (error as { error?: { message?: string; error?: { message?: string } } })?.error;
    return body?.message ?? body?.error?.message ?? null;
  }
}
